import React, { useContext, useMemo, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Tooltip, 
  useTheme
} from '@mui/material';
import { tableStyles } from '../theme/tableStyles';
import { cellStyles, typography } from '../theme/typography';
import { InventoryContext } from './Inventory';
import { allSeeds, calculateBpPerMinute } from '../data/seedData';
import { BED_DATA } from './Beds';
import RarityAvatar from '../components/common/RarityAvatar';
import { foodItems, getFoodImagePath } from '../data/foodData';
import { animalProducts } from '../data/productData';
import { allAnimals } from '../data/animalData';
import { getRarityMultiplier } from '../utils/rarityUtils';

const bedRarities = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];

function formatGrowTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function flattenSeeds(inventorySeeds) {
  const result = [];
  if (!inventorySeeds) {
    console.warn('No inventory seeds provided to flattenSeeds');
    return result;
  }
  
  // Process all seed categories including limitedSeeds
  Object.entries(allSeeds).forEach(([season, seeds]) => {
    if (!Array.isArray(seeds)) {
      console.warn(`Expected array for seed category ${season}, got:`, seeds);
      return;
    }
    
    console.log(`Processing seed category: ${season}, found ${seeds.length} seeds`);
    
    seeds.forEach(seed => {
      if (!seed.rarity || !Array.isArray(seed.rarity)) {
        console.warn(`Invalid or missing rarity for seed ${seed.id}`, seed);
        return;
      }
      
      seed.rarity.forEach(r => {
        if (!r || !r.type) {
          console.warn(`Invalid rarity data for seed ${seed.id}`, r);
          return;
        }
        
        // Try multiple key formats for maximum compatibility
        const keyFormats = [
          `${seed.id}-${r.type}`,                    // black_lotus-Legendary
          `${seed.id}_${r.type}`.toLowerCase(),      // black_lotus_legendary
          `${seed.id}_${r.type.toLowerCase()}`,      // black_lotus_legendary (alternative)
          `${seed.id}_${r.type.split(' ').join('_').toLowerCase()}`  // handles multi-word rarities
        ];
        
        let count = 0;
        for (const key of keyFormats) {
          if (inventorySeeds[key] > 0) {
            count = inventorySeeds[key];
            break;
          }
        }
        
        if (count > 0) {
          const bpPerMin = calculateBpPerMinute ? calculateBpPerMinute(r.biopoints, r.growTime) : (r.biopoints / r.growTime);
          const seedData = {
            id: seed.id,
            name: seed.name,
            rarity: r.type,
            count: count,
            biopoints: r.biopoints,
            growTime: r.growTime,
            image: `/images/seeds/${seed.id}/${r.type.toLowerCase().split(' ').join('_')}/preview.png`,
            bpPerMin,
            key: `${seed.id}-${r.type}` // Add a consistent key for lookups
          };
          
          // Only log limited seeds or if we find a black lotus
          if (season === 'limitedSeeds' || seed.id.includes('black_lotus')) {
            console.log('Adding limited seed to recommendations:', {
              season,
              id: seed.id,
              name: seed.name,
              rarity: r.type,
              count,
              key: `${seed.id}-${r.type}`
            });
          }
          result.push(seedData);
        }
      });
    });
  });
  
  console.log('Total flattened seeds:', result.length);
  return result;
}

function flattenBeds(inventoryBeds) {
  const result = [];
  BED_DATA.forEach(bed => {
    const count = inventoryBeds[bed.rarity] || 0;
    for (let i = 0; i < count; i++) {
      result.push({
        rarity: bed.rarity,
        name: bed.name,
        image: bed.image,
        productAmount: parseInt(bed.productAmount.replace('x', '')) || 1,
        originalIndex: result.length // Track original index
      });
    }
  });
  return result;
}

const RARITY_ORDER = {
  'Common': 0,
  'Uncommon': 1,
  'Rare': 2,
  'Epic': 3,
  'Legendary': 4
};

const getHighestFoodForAnimal = (animalKey, inventorySeeds, inventoryAnimals) => {
  const foodList = foodItems[animalKey]?.food || [];
  console.log(`Checking food for ${animalKey}. Available food rarities:`, foodList.map(f => f.rarity));
  
  // Try from highest rarity to lowest
  for (let i = foodList.length - 1; i >= 0; i--) {
    const food = foodList[i];
    console.log(`Checking ${animalKey} food rarity: ${food.rarity}`);
    
    const canMake = food.seeds.every(req => {
      const baseName = req.name.toLowerCase().replace(/ /g, '_');
      const requiredRarity = req.rarity;
      const requiredAmount = req.amount || 1;
      
      // Special handling for animal products (like Milk for Piggies)
      if (baseName === 'milk' || baseName === 'eggs' || baseName === 'wool' || baseName === 'fuzzies') {
        console.log(`  Checking if we can produce animal product: ${baseName} (${requiredRarity})`);
        
        // Check if we can produce this animal product with our current animals
        const canProduce = canProduceAnimalProduct(baseName, requiredRarity, inventorySeeds, inventoryAnimals);
        
        console.log(`    Can produce ${baseName} ${requiredRarity}: ${canProduce}`);
        return canProduce;
      }
      
      // Get all possible rarities that are equal to or higher than required
      const possibleRarities = Object.entries(RARITY_ORDER)
        .filter(([rarity]) => RARITY_ORDER[rarity] >= RARITY_ORDER[requiredRarity])
        .sort((a, b) => RARITY_ORDER[b[0]] - RARITY_ORDER[a[0]])
        .map(([rarity]) => rarity);
      
      console.log(`  Checking seed: ${baseName} (${requiredRarity}+), possible rarities:`, possibleRarities);
      
      // Try each possible rarity from highest to lowest
      for (const rarity of possibleRarities) {
        // Try both case variations of the key
        const key1 = `${baseName}-${rarity.toLowerCase()}`;
        const key2 = `${baseName}-${rarity}`;
        
        // Check if we have at least one seed of this type
        // Since one seed can be planted and harvested multiple times, we only need 1 seed
        const hasSeed = (inventorySeeds[key1] || inventorySeeds[key2] || 0) > 0;
        
        console.log(`    Trying ${rarity} with keys: ${key1}, ${key2}`);
        console.log(`    Found seed: ${hasSeed}, Need at least 1 for ${requiredAmount}x ${baseName}`);
        
        if (hasSeed) {
          console.log(`    ✓ Found ${rarity} ${baseName} seed for ${food.rarity} food`);
          return true;
        }
      }
      
      console.log(`    ✗ Not enough ${baseName} of required rarity ${requiredRarity} or higher`);
      return false;
    });
    
    console.log(`Can make ${food.rarity} food for ${animalKey}: ${canMake}`);
    if (canMake) return food;
  }
  
  console.log(`No suitable food found for ${animalKey}`);
  return null;
};

// Check if we can produce a specific animal product with our current animals and seeds
const canProduceAnimalProduct = (productName, requiredRarity, inventorySeeds, inventoryAnimals) => {
  console.log(`\n=== Checking production capability for ${productName} ${requiredRarity} ===`);
  
  // Find which animal produces this product
  const productInfo = animalProducts.find(p => p.id === productName);
  if (!productInfo) {
    console.log(`Product ${productName} not found in animalProducts`);
    return false;
  }
  
  console.log(`Product ${productName} is produced by: ${productInfo.sourceAnimals.join(', ')}`);
  
  // Check each animal type that can produce this product
  for (const animalType of productInfo.sourceAnimals) {
    console.log(`\n--- Checking ${animalType} for ${productName} ${requiredRarity} ---`);
    
    // Use standardized animal keys consistently across all components
    let animalKey = animalType.toLowerCase(); // birds, cattle, fuzzies, piggies
    
    // Try different key formats for animals across all rarities
    const rarities = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
    let hasRequiredAnimal = false;
    
    for (const rarity of rarities) {
      const possibleAnimalKeys = [
        `${animalKey}-${rarity.toLowerCase()}`,
        `${animalKey}-${rarity}`,
        `${animalKey}-${rarity.toLowerCase()}`,
        `${animalKey}-${rarity}`
      ];
      
      for (const key of possibleAnimalKeys) {
        if ((inventoryAnimals?.[key] || 0) > 0) {
          hasRequiredAnimal = true;
          console.log(`    ✓ Found required animal: ${key} (${inventoryAnimals[key]} units)`);
          break;
        }
      }
      if (hasRequiredAnimal) break;
    }
    
    if (!hasRequiredAnimal) {
      console.log(`    ✗ No ${animalType} of any rarity found in inventory`);
      continue; // Try next animal type
    }
    
    // Check if we have food of the REQUIRED rarity for this animal (use standardized key)
    const foodForAnimal = getHighestFoodForAnimal(animalKey, inventorySeeds, inventoryAnimals);
    if (foodForAnimal && foodForAnimal.rarity === requiredRarity) {
      console.log(`    ✓ Can produce food for ${animalType}: ${foodForAnimal.name} (${foodForAnimal.rarity})`);
      console.log(`    ✓ ${animalType} + ${foodForAnimal.rarity} food can produce ${productName} ${requiredRarity}`);
      return true;
    } else {
      console.log(`    ✗ Cannot produce ${requiredRarity} food for ${animalType} (found: ${foodForAnimal?.rarity || 'none'})`);
    }
  }
  
  console.log(`=== Cannot produce ${productName} ${requiredRarity} ===\n`);
  return false;
};

const getProductBiopoints = (animalKey, rarity) => {
  console.log(`Getting biopoints for ${animalKey} with rarity ${rarity}`);
  
  const productMap = { 
    birds: 'eggs', 
    cattle: 'milk', 
    fuzzies: 'wool', 
    piggies: 'truffle' 
  };
  
  const productId = productMap[animalKey];
  const product = animalProducts.find(p => p.id === productId);
  
  if (!product) {
    console.warn(`No product found for animal key: ${animalKey}`);
    return 0; // Return 0 instead of null for consistency
  }
  
  // For Truffle, we only have Common variant, so always return its biopoints
  if (productId === 'truffle') {
    return product.variants[0]?.biopoints || 0;
  }
  
  // Debug log available variants
  console.log(`Available variants for ${productId}:`, product.variants.map(v => `${v.rarity} (${v.biopoints} bp)`));
  
  // For other products, find matching rarity (case-insensitive)
  const targetRarity = String(rarity).toLowerCase();
  const variant = product.variants.find(v => 
    v.rarity.toLowerCase() === targetRarity
  );
  
  if (!variant) {
    console.warn(`No product variant found for ${productId} with rarity ${rarity}. Available rarities:`, 
      product.variants.map(v => v.rarity).join(', ')
    );
    // Return first variant's biopoints as fallback, or 0 if no variants exist
    return product.variants[0]?.biopoints || 0;
  }
  
  console.log(`Found variant for ${productId} (${rarity}):`, variant);
  return variant.biopoints;
};

// Function to calculate direct Common costs for missing rarities
const FOOD_SEED_TYPES = ['corn', 'peas', 'cabbage', 'radish'];
const RARITIES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
const RARITY_COSTS = [1, 2, 4, 8, 16]; // 2^0, 2^1, 2^2, 2^3, 2^4

// Map of animal types to their variant names in inventory - NO LONGER NEEDED
// const ANIMAL_VARIANT_MAP = {
//   'birds': 'chicken',
//   'cattle': 'cow',
//   'fuzzies': 'sheep',
//   'piggies': 'pig'
// };

// Function to calculate direct Common costs for missing rarities
const calculateDirectFusionCosts = (inventory) => {
  if (!inventory?.seeds) return [];

  return FOOD_SEED_TYPES.map(seedType => {
    const currentState = {};
    let workingState = {};
    let totalCommonNeeded = 0;

    // Get current counts for each rarity
    RARITIES.forEach(rarity => {
      const seedKey = `${seedType}-${rarity}`;
      currentState[rarity] = inventory.seeds[seedKey] || 0;
    });

    // Start with current state
    workingState = { ...currentState };

    // Simulate fusion process
    // 1. Fuse Rare to Epic if possible
    if (workingState['Rare'] >= 2) {
      workingState['Rare'] -= 2;
      workingState['Epic'] += 1;
    }

    // 2. Fuse Epic to Legendary if possible
    if (workingState['Epic'] >= 2) {
      workingState['Epic'] -= 2;
      workingState['Legendary'] += 1;
    }

    // 3. Calculate direct Common costs for missing rarities after fusion
    for (let i = 0; i < RARITIES.length; i++) {
      const rarity = RARITIES[i];
      const currentAfterFusion = workingState[rarity];
      const target = 1;

      if (currentAfterFusion < target) {
        const needed = target - currentAfterFusion;
        const commonCost = needed * RARITY_COSTS[i];
        totalCommonNeeded += commonCost;
      }
    }

    // Check if any fusion occurred
    const fusionOccurred = workingState['Rare'] !== currentState['Rare'] || 
                          workingState['Epic'] !== currentState['Epic'] ||
                          workingState['Legendary'] !== currentState['Legendary'];

    return {
      type: seedType,
      current: currentState,
      afterFusion: workingState,
      totalCommonNeeded,
      canComplete: totalCommonNeeded <= currentState['Common'],
      fusionOccurred
    };
  });
};

const getProductGrowTime = (animalKey, rarity) => {
  console.log(`Getting grow time for ${animalKey} with rarity ${rarity}`);
  
  const productMap = { 
    birds: 'eggs', 
    cattle: 'milk', 
    fuzzies: 'wool', 
    piggies: 'truffle' 
  };
  
  const productId = productMap[animalKey];
  const product = animalProducts.find(p => p.id === productId);
  
  if (!product) {
    console.warn(`No product found for animal key: ${animalKey}`);
    return 0;
  }
  
  // For Truffle, we only have Common variant, so always return its grow time
  if (productId === 'truffle') {
    return product.variants[0]?.productionTime || 0;
  }
  
  // Debug log available variants
  console.log(`Available variants for ${productId}:`, product.variants.map(v => `${v.rarity} (${v.productionTime}s)`));
  
  // For other products, find matching rarity (case-insensitive)
  const targetRarity = String(rarity).toLowerCase();
  const variant = product.variants.find(v => 
    v.rarity.toLowerCase() === targetRarity
  );
  
  if (!variant) {
    console.warn(`No product variant found for ${productId} with rarity ${rarity}. Available rarities:`, 
      product.variants.map(v => v.rarity).join(', ')
    );
    // Return first variant's grow time as fallback, or 0 if no variants exist
    return product.variants[0]?.productionTime || 0;
  }
  
  console.log(`Found variant for ${productId} (${rarity}):`, variant);
  return variant.productionTime;
};

const Recommendations = () => {
  const { inventory } = useContext(InventoryContext);
  const theme = useTheme();

  // Debug: Log inventory state when it changes
  useEffect(() => {
    if (inventory?.seeds) {
      console.log('Current inventory seeds:', Object.keys(inventory.seeds));
      console.log('Seed counts:', Object.entries(inventory.seeds)
        .filter(([_, count]) => count > 0)
        .map(([key, count]) => `${key}: ${count}`)
        .join(', ')
      );
    }
  }, [inventory]);

  // Move getUpgradeCost to top-level scope
  function getUpgradeCost(toRarity) {
    const bed = BED_DATA.find(b => b.rarity === toRarity);
    let fert = 0, cfb = 0;
    if (bed && bed.requirements) {
      bed.requirements.forEach(req => {
        if (req.type === 'fertilizer') fert += parseInt(req.value);
        if (req.type === 'cfb') cfb += parseInt(req.value);
      });
    }
    return { fert, cfb };
  }

  // Listen for inventory updates from file loading
  useEffect(() => {
    const handleInventoryUpdate = (event) => {
      // The inventory context should automatically update, but this ensures it
      // Debug logging removed
    };
    
    window.addEventListener('inventoryUpdated', handleInventoryUpdate);
    
    return () => {
      window.removeEventListener('inventoryUpdated', handleInventoryUpdate);
    };
  }, []);

  const generateRecommendations = (seeds, beds, strategy) => {
    console.log('Generating recommendations with seeds:', seeds);
    const seedCounts = {};
    seeds.forEach(s => { 
      const key = `${s.id}-${s.rarity}`;
      seedCounts[key] = s.count;
      if (s.id.includes('black_lotus')) {
        console.log('Black Lotus found in seeds:', { id: s.id, rarity: s.rarity, count: s.count });
      }
    });
    
    const assignments = [];
    let totalBiopoints = 0;
    let totalProducts = 0;
    
    console.log('Available beds:', beds.length);
    
    if (strategy === 'active') {
      // --- Active Playing: maximize BP/min with minimum 1h grow time ---
      // Filter seeds with minimum 1 hour grow time
      const eligibleSeeds = seeds.filter(seed => {
        const growTimeHours = (seed.growTime || 0) / 3600; // Convert seconds to hours
        return growTimeHours >= 1; // Seeds that take AT LEAST 1 hour
      });
      
      const filteredOutCount = seeds.length - eligibleSeeds.length;
      console.log(`Active farming: ${eligibleSeeds.length} seeds available, ${filteredOutCount} filtered out (grow time < 1h)`);
      
      beds.forEach(bed => {
        let best = null;
        eligibleSeeds.forEach(seed => {
          const key = `${seed.id}-${seed.rarity}`;
          if (seedCounts[key] > 0) {
            // Calculate BP per minute for this seed
            const bpPerMin = calculateBpPerMinute(seed, seed.rarity);
            const total = bed.productAmount * seed.biopoints;
            
            console.log(`Seed ${seed.name}: ${(seed.growTime || 0) / 3600}h grow time, ${bpPerMin.toFixed(2)} BP/min`);
            
            if (!best || bpPerMin > best.bpPerMin) {
              best = { 
                seed, 
                total,
                bpPerMin,
                key 
              };
            }
          }
        });
        
        if (best) {
          console.log(`Assigning best seed for active farming bed ${bed.rarity}:`, {
            seedId: best.seed.id,
            seedRarity: best.seed.rarity,
            growTimeHours: (best.seed.growTime || 0) / 3600,
            bpPerMin: best.bpPerMin.toFixed(2),
            remaining: seedCounts[best.key] - 1
          });
          
          assignments.push({
            plot: bed,
            seed: best.seed,
            total: best.total
          });
          
          seedCounts[best.key]--;
          totalBiopoints += best.total;
          totalProducts++;
        }
      });
    } else {
      // --- Passive Farming: assign by biopoints, respect inventory ---
      beds.forEach((bed, i) => {
        const availableSeed = seeds.find(s => {
          const key = `${s.id}-${s.rarity}`;
          return seedCounts[key] > 0;
        });
        
        if (availableSeed) {
          const key = `${availableSeed.id}-${availableSeed.rarity}`;
          console.log(`Assigning seed to passive farming bed ${i+1}:`, {
            bedRarity: bed.rarity,
            seedId: availableSeed.id,
            seedRarity: availableSeed.rarity,
            remaining: seedCounts[key] - 1
          });
          
          assignments.push({
            plot: bed,
            seed: availableSeed,
          });
          
          seedCounts[key]--;
          totalBiopoints += (bed.productAmount * availableSeed.biopoints);
          totalProducts++;
        }
      });
    }
    
    console.log('Final assignments:', assignments.map(a => ({
      bed: a.plot.rarity,
      seed: a.seed.name,
      rarity: a.seed.rarity
    })));
    
    return { assignments, totalBiopoints, totalProducts };
  };

  const recommendations = useMemo(() => {
    if (!inventory) return null;
    const availableSeeds = flattenSeeds(inventory.seeds);
    let availableBeds = flattenBeds(inventory.beds);
    availableBeds = availableBeds.sort((a, b) => b.productAmount - a.productAmount);

    // --- Active Maintenance: assign by BP/min, respect inventory ---
    const activeSeeds = [...availableSeeds].sort((a, b) => b.bpPerMin - a.bpPerMin);
    const activeSeedCounts = {};
    activeSeeds.forEach(seed => { activeSeedCounts[`${seed.id}-${seed.rarity}`] = seed.count; });
    const activeAssignments = [];
    let activeTotalBiopoints = 0;
    availableBeds.forEach(bed => {
      let best = null;
      activeSeeds.forEach(seed => {
        if (activeSeedCounts[`${seed.id}-${seed.rarity}`] > 0) {
          const total = bed.productAmount * seed.biopoints;
          if (!best || seed.bpPerMin > best.bpPerMin) {
            best = { seed, total, bpPerMin: seed.bpPerMin, key: `${seed.id}-${seed.rarity}` };
          }
        }
      });
      if (best) {
        activeAssignments.push({ plot: bed, seed: best.seed, total: best.total });
        activeTotalBiopoints += best.total;
        activeSeedCounts[best.key]--;
      }
    });

    // --- Passive Farming: assign by biopoints, respect inventory ---
    const passiveSeeds = [...availableSeeds].sort((a, b) => b.biopoints - a.biopoints);
    const passiveSeedCounts = {};
    passiveSeeds.forEach(seed => { passiveSeedCounts[`${seed.id}-${seed.rarity}`] = seed.count; });
    const passiveAssignments = [];
    let passiveTotalBiopoints = 0;
    availableBeds.forEach(bed => {
      let best = null;
      passiveSeeds.forEach(seed => {
        if (passiveSeedCounts[`${seed.id}-${seed.rarity}`] > 0) {
          const total = bed.productAmount * seed.biopoints;
          if (!best || total > best.total) {
            best = { seed, total, key: `${seed.id}-${seed.rarity}` };
          }
        }
      });
      if (best) {
        passiveAssignments.push({ plot: bed, seed: best.seed, total: best.total });
        passiveTotalBiopoints += best.total;
        passiveSeedCounts[best.key]--;
      }
    });

    return {
      active: { assignments: activeAssignments, totalBiopoints: activeTotalBiopoints },
      passive: { assignments: passiveAssignments, totalBiopoints: passiveTotalBiopoints }
    };
  }, [inventory]);

  function getTrueOptimalUpgradePath() {
    if (!recommendations || !recommendations.passive) return null;
    const passiveSeeds = [...flattenSeeds(inventory.seeds)].sort((a, b) => b.biopoints - a.biopoints);
    const bedCounts = bedRarities.reduce((acc, rarity) => {
      acc[rarity] = (inventory.beds && inventory.beds[rarity]) || 0;
      return acc;
    }, {});
    function recurse(beds, steps, totalFert, totalFri) {
    let finalBeds = [];
    bedRarities.forEach(rarity => {
      const bedInfo = BED_DATA.find(b => b.rarity === rarity);
      if (bedInfo) {  // Only proceed if bedInfo is found
        for (let i = 0; i < (beds[rarity] || 0); i++) {
          finalBeds.push({
            rarity,
            name: bedInfo.name,
            image: bedInfo.image,
            productAmount: parseInt(bedInfo.productAmount.replace('x', '')) || 1,
            originalIndex: finalBeds.length
          });
        }
      } else {
        console.warn(`No bed info found for rarity: ${rarity}`);
      }
    });
    finalBeds = finalBeds.sort((a, b) => b.productAmount - a.productAmount);
    const upgradedRecommendation = generateRecommendations(passiveSeeds, finalBeds, 'passive');
    const currentTotal = recommendations.passive.totalBiopoints;
    const newTotal = upgradedRecommendation.totalBiopoints;
      let best = {
        steps: [...steps],
      totalFert,
      totalFri,
      currentTotal,
      newTotal,
      gain: newTotal - currentTotal,
        finalBeds: { ...beds },
        assignments: upgradedRecommendation.assignments
      };
      // Only consider upgrades for rarities you actually own (at least 2 beds)
      for (let r = 0; r < bedRarities.length - 1; r++) {
        const from = bedRarities[r];
        const to = bedRarities[r + 1];
        const count = beds[from];
        if (count >= 2) {
          // Branch 1: perform the upgrade
          const { fert, cfb } = getUpgradeCost(to);
          const newBeds = { ...beds };
          newBeds[from] -= 2;
          newBeds[to] += 1;
          const newSteps = [...steps, {
            from,
            to,
            count: 2,
            newBeds: 1,
            fert: fert,
            cfb: cfb
          }];
          const resultUpgrade = recurse(newBeds, newSteps, totalFert + fert, totalFri + cfb);
          if (resultUpgrade && resultUpgrade.gain > best.gain) {
            // Debug logging removed
            best = resultUpgrade;
          }
          // Skip this upgrade and try next possible upgrade
          // (by continuing the loop without upgrading this pair)
          // This is handled by not returning early and letting the loop continue
        }
      }
      // Debug logging removed
      return best;
    }
    // Always compare the current config to all possible upgrades, including not upgrading at all
    return recurse(bedCounts, [], 0, 0);
  }
  const trueOptimalUpgradePath = useMemo(getTrueOptimalUpgradePath, [recommendations, inventory]);
  
  // Generate recommendations based on passive farming (max biopoints)
  const recommendation = useMemo(() => {
    if (!inventory) return null;
    
    let availableSeeds = flattenSeeds(inventory.seeds);
    let availableBeds = flattenBeds(inventory.beds);
    
    // For passive farming, sort by total biopoints (highest first)
    availableSeeds = [...availableSeeds].sort((a, b) => b.biopoints - a.biopoints);
    
    // Sort beds by product amount (highest first)
    availableBeds = availableBeds.sort((a, b) => b.productAmount - a.productAmount);
    
    const result = generateRecommendations(availableSeeds, availableBeds, 'passive');
    
    return {
      ...result,
      type: 'passive',
      description: 'Maximizes biopoints per harvest (best for passive farming)'
    };
  }, [inventory]);

  const renderRecommendationTable = (title, recommendation, strategy) => {
    if (!recommendation || !recommendation.assignments || recommendation.assignments.length === 0) {
      return (
        <Typography component="div" variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
          No available seeds and plots to recommend for {strategy} strategy.
        </Typography>
      );
    }

    // Calculate total biopoints for all assignments
    const totalBiopoints = recommendation.assignments.reduce((total, a) => {
      return total + (a.plot.productAmount * a.seed.biopoints);
    }, 0);

    return (
      <Box sx={{ width: 1200, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'primary.main' }}>
            {title}
          </Typography>
        </Box>
        <TableContainer sx={{ 
          background: 'transparent', 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          overflow: 'hidden',
          tableLayout: 'fixed',
          minWidth: '100%',
          '& .MuiTableHead-root .MuiTableCell-root': {
            py: 1,
            px: 2,
            height: 40,
            maxHeight: 40,
            padding: '8px 16px',
            width: '16.666%' // 100% / 6 columns
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            width: '16.666%' // 100% / 6 columns
          }
        }}>
          <Table size="small" sx={{ width: 'auto', minWidth: '100%', background: 'transparent' }}>
            <TableHead sx={tableStyles(theme).tableHead}>
              <TableRow>
                <TableCell sx={{ ...tableStyles(theme).tableCell, ...cellStyles.header, textAlign: 'center' }}>Plot</TableCell>
                <TableCell sx={{ ...tableStyles(theme).tableCell, ...cellStyles.header, textAlign: 'center' }}>Seed</TableCell>
                <TableCell sx={{ ...tableStyles(theme).tableCell, ...cellStyles.header, textAlign: 'center' }}>Grow Time</TableCell>
                <TableCell sx={{ ...tableStyles(theme).tableCell, ...cellStyles.header, textAlign: 'center' }}>Products</TableCell>
                <TableCell sx={{ ...tableStyles(theme).tableCell, ...cellStyles.header, textAlign: 'center' }}>Biopoints/Seed</TableCell>
                <TableCell sx={{ ...tableStyles(theme).tableCell, ...cellStyles.header, textAlign: 'center' }}>Biopoints/Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recommendation.assignments.map((a, idx) => {
                const products = a.plot.productAmount;
                const biopointsPerSeed = a.seed.biopoints;
                const biopointsTotal = products * biopointsPerSeed;
                return (
                  <TableRow key={idx}>
                    <TableCell align="center" sx={{ borderLeft: '1px solid', borderRight: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, width: '100%' }}>
                        <Tooltip title={`${a.plot.name} (${a.plot.rarity})`} arrow>
                          <RarityAvatar src={a.plot.image} alt={a.plot.name} size={40} sx={{ width: 40, height: 40 }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, width: '100%' }}>
                        <Tooltip title={`${a.seed.name} (${a.seed.rarity})`} arrow>
                          <RarityAvatar src={a.seed.image} alt={a.seed.name} size={40} sx={{ width: 40, height: 40 }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={typography.number}>{formatGrowTime(a.seed.growTime)}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={typography.number}>{products}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={typography.number}>{biopointsPerSeed}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={typography.number}>{biopointsTotal}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* Total Biopoints Summary */}
          {totalBiopoints > 0 && (
            <Table>
              <TableBody>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell 
                    colSpan={7} 
                    align="center" 
                    sx={{ 
                      fontWeight: 'bold', 
                      p: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      '&.MuiTableCell-root': {
                        display: 'table-cell',
                        width: '100%',
                        maxWidth: '100%'
                      }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      Total Biopoints from Seeds: {totalBiopoints.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>
    );
  };

  // Find the best single upgrade step possible from current inventory
  function getBestImmediateUpgradeStep() {
    if (!inventory) return null;
    const bedCounts = bedRarities.reduce((acc, rarity) => {
      acc[rarity] = (inventory.beds && inventory.beds[rarity]) || 0;
      return acc;
    }, {});
    let bestStep = null;
    let bestGain = -Infinity;
    let bestAssignments = null;
    for (let r = 0; r < bedRarities.length - 1; r++) {
      const from = bedRarities[r];
      const to = bedRarities[r + 1];
      const count = bedCounts[from];
      if (count >= 2) {
        // Simulate this single upgrade
        const { fert, cfb } = getUpgradeCost(to);
        const newBeds = { ...bedCounts };
        newBeds[from] -= 2;
        newBeds[to] += 1;
        // Build the bed array
        let bedsArr = [];
        bedRarities.forEach(rarity => {
          const bedInfo = BED_DATA.find(b => b.rarity === rarity);
          for (let i = 0; i < newBeds[rarity]; i++) {
            bedsArr.push({
              rarity,
              name: bedInfo.name,
              image: bedInfo.image,
              productAmount: parseInt(bedInfo.productAmount.replace('x', '')) || 1,
              originalIndex: bedsArr.length
            });
          }
        });
        bedsArr = bedsArr.sort((a, b) => b.productAmount - a.productAmount);
        const rec = generateRecommendations(
          [...flattenSeeds(inventory.seeds)].sort((a, b) => b.biopoints - a.biopoints),
          bedsArr,
          'passive'
        );
        // Calculate gain: difference between this config and original config
        let origBedsArr = [];
        bedRarities.forEach(rarity => {
          const bedInfo = BED_DATA.find(b => b.rarity === rarity);
          for (let i = 0; i < bedCounts[rarity]; i++) {
            origBedsArr.push({
              rarity,
              name: bedInfo.name,
              image: bedInfo.image,
              productAmount: parseInt(bedInfo.productAmount.replace('x', '')) || 1,
              originalIndex: origBedsArr.length
            });
          }
        });
        origBedsArr = origBedsArr.sort((a, b) => b.productAmount - a.productAmount);
        const origRec = generateRecommendations(
          [...flattenSeeds(inventory.seeds)].sort((a, b) => b.biopoints - a.biopoints),
          origBedsArr,
          'passive'
        );
        const gain = rec.totalBiopoints - origRec.totalBiopoints;
        if (gain > bestGain) {
          bestGain = gain;
          bestStep = { from, to, count: 2, newBeds: 1, fert, cfb, gain, totalBiopoints: rec.totalBiopoints };
          bestAssignments = rec.assignments;
        }
      }
    }
    if (bestStep && bestGain > 0) {
      return { ...bestStep, assignments: bestAssignments };
    }
    return null;
  }
  const bestImmediateUpgrade = useMemo(getBestImmediateUpgradeStep, [inventory]);

  return (
    <Box sx={{ width: 1200, mx: 'auto', mt: 4, mb: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Farming Recommendations - Optimized */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 4 }}>
          <Typography component="div" variant="h6" sx={{ color: 'primary.main', textAlign: 'center' }}>
            Farming Schedule & Login Times
          </Typography>
        </Box>
        
        {/* Strategy Description */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography component="div" variant="body2" color="text.secondary">
            {recommendation?.description || 'Generating optimal planting schedule...'}
          </Typography>
        </Box>
        {/* Placeholder for Full active play if no assignments, to prevent table from moving up */}
        {(!recommendation || !recommendation.assignments || recommendation.assignments.length === 0) ? (
          <Typography component="div" variant="body2" sx={{ textAlign: 'center', mt: 2, p: 2, borderRadius: 1 }}>
            No seeds and plots available for farming recommendations.
            <br />
            <Typography variant="caption" color="text.secondary">
              Farming recommendations maximize total BP per harvest cycle.
            </Typography>
          </Typography>
        ) : (
          <>
            {renderRecommendationTable('', recommendation, 'passive')}
          </>
        )}
      </Box>
      {/* Food Seed Requirements Section */}
      <Box sx={{ width: 1200, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'primary.main' }}>
          Animal Products & Food Requirements (per Animal Type)
        </Typography>
        <TableContainer sx={{ 
          background: 'transparent', 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 2, 
          boxShadow: 0, 
          width: '100%',
          maxWidth: 1200,
          mx: 'auto', 
          overflowX: 'auto',
          '& .MuiTable-root': {
            width: 'auto',
            minWidth: '100%',
            tableLayout: 'auto',
            background: 'transparent'
          },
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
            p: 1
          }
        }}>
          <Table size="small" sx={{ width: 'auto', minWidth: '100%', background: 'transparent' }}>
            <TableHead sx={tableStyles(theme).tableHead}>
              <TableRow>
                <TableCell align="center" sx={{ minWidth: 100, whiteSpace: 'normal', p: 1 }}>Animal</TableCell>
                <TableCell align="center" sx={{ minWidth: 100, whiteSpace: 'normal', p: 1 }}>Food</TableCell>
                <TableCell align="center" sx={{ minWidth: 90, whiteSpace: 'nowrap', p: 1 }}>Food for 24h</TableCell>
                <TableCell align="center" sx={{ minWidth: 100, whiteSpace: 'nowrap', p: 1 }}>Merges for 24h</TableCell>
                <TableCell align="center" sx={{ minWidth: 100, whiteSpace: 'nowrap', p: 1 }}>Seeds for 24h</TableCell>
                <TableCell align="center" sx={{ minWidth: 100, whiteSpace: 'normal', p: 1 }}>Product</TableCell>
                <TableCell align="center" sx={{ minWidth: 90, whiteSpace: 'nowrap', p: 1 }}>Grow Time</TableCell>
                <TableCell align="center" sx={{ minWidth: 100, whiteSpace: 'nowrap', p: 1 }}>Products in 24h</TableCell>
                <TableCell align="center" sx={{ minWidth: 120, whiteSpace: 'nowrap', p: 1 }}>Biopoints/Product</TableCell>
                <TableCell align="center" sx={{ minWidth: 120, whiteSpace: 'nowrap', p: 1 }}>Biopoints/Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                let totalAnimalBiopoints = 0;
                const rows = ['birds', 'cattle', 'fuzzies', 'piggies'].map(animalKey => {
                  // Get the food item for this animal
                  let food = getHighestFoodForAnimal(animalKey, inventory.seeds, inventory.animals);
                  
                  // Special handling for Piggies: they can only make Common food, not the highest available
                  if (animalKey === 'piggies' && foodItems[animalKey]?.food?.length > 0) {
                    food = foodItems[animalKey].food.find(f => f.rarity === 'Common') || foodItems[animalKey].food[0];
                  }
                  
                  let hasAllSeeds = false;
                  let missingSeeds = [];
                  
                  // If no food found, use the first available food for this animal
                  // This applies to all animals including piggies
                  if (!food && foodItems[animalKey]?.food?.length > 0) {
                    food = foodItems[animalKey].food[0];
                    hasAllSeeds = false;
                    // Mark all seeds as missing
                    missingSeeds = food.seeds.map(seed => ({
                      name: seed.name,
                      rarity: seed.rarity,
                      amount: seed.amount
                    }));
                  }
                  
                  if (food) {
                    // Check if we have all required seed TYPES for the food
                    // The amount is how many are needed to make the food, not to unlock biopoints
                    hasAllSeeds = true;
                    missingSeeds = []; // Reset missing seeds
                    
                    console.log(`\n=== Checking seeds for ${animalKey} (${food.rarity} food) ===`);
                    console.log('Available inventory seeds:', Object.entries(inventory.seeds || {})
                      .filter(([_, count]) => count > 0)
                      .map(([key, count]) => `${key}: ${count}`)
                      .join(', ')
                    );
                    console.log('Available inventory animals:', Object.entries(inventory.animals || {})
                      .filter(([_, count]) => count > 0)
                      .map(([key, count]) => `${key}: ${count}`)
                      .join(', ')
                    );
                    
                    for (const seed of food.seeds) {
                      // Create a case-insensitive check by normalizing the keys
                      const seedNameLower = seed.name.toLowerCase();
                      const rarityLower = seed.rarity.toLowerCase();
                      let hasSeed = false;
                      
                      // Special handling for animal products (like Milk) vs regular seeds
                      if (seedNameLower === 'milk' || seedNameLower === 'eggs' || seedNameLower === 'wool') {
                        // This is an animal product, check if we can produce it
                        console.log(`  Checking animal product: ${seed.name} ${seed.rarity}`);
                        hasSeed = canProduceAnimalProduct(seedNameLower, seed.rarity, inventory.seeds, inventory.animals);
                        console.log(`    Can produce ${seed.name} ${seed.rarity}: ${hasSeed}`);
                      } else {
                        // This is a regular seed, check inventory
                        const matchingKeys = Object.keys(inventory.seeds || {}).filter(key => {
                          const [name, rarity] = key.split('-');
                          return name.toLowerCase() === seedNameLower.toLowerCase() && 
                                 rarity.toLowerCase() === rarityLower;
                        });
                        
                        // Check if any of the matching keys have a count > 0
                        hasSeed = matchingKeys.some(key => (inventory.seeds[key] || 0) > 0);
                        
                        // For debugging
                        console.log(`  Seed check (${seed.name} ${seed.rarity}):`);
                        console.log(`    Tried keys: ${matchingKeys.join(', ') || 'none'}`);
                        console.log(`    Found: ${matchingKeys.length > 0 ? 'Yes' : 'No'}`);
                      }
                      
                      
                      if (!hasSeed) {
                        hasAllSeeds = false;
                        missingSeeds.push({
                          name: seed.name,
                          rarity: seed.rarity,
                          amount: 1, // Only need 1 of each type to unlock biopoints
                          available: 0
                        });
                      }
                    }
                    
                    console.log(`=== ${animalKey} - Has all seeds: ${hasAllSeeds} ===\n`);
                    
                    // Debug log
                    console.log(`Animal: ${animalKey}, hasAllSeeds: ${hasAllSeeds}`, {
                      foodRarity: food.rarity,
                      seeds: food.seeds,
                      missingSeeds,
                      inventorySeeds: Object.entries(inventory.seeds || {})
                        .filter(([k, v]) => v > 0)
                        .map(([k, v]) => `${k}: ${v}`)
                    });
                  }
                  
                  // For all animals, ensure we have a food item to display
                  if (!food) {
                    const defaultFood = foodItems[animalKey]?.food?.[0];
                    if (defaultFood) {
                      food = defaultFood;
                      hasAllSeeds = false;
                      missingSeeds = [...(defaultFood.seeds || [])];
                    }
                  }
                  
                  // Calculate biopoints and grow time
                  // We only need one of each seed type to get full biopoints
                  let biopoints = 0;
                  let growTime = 0;
                  
                  if (food) {
                    // Get full biopoints and grow time if we have all required seed types
                    if (hasAllSeeds) {
                      biopoints = getProductBiopoints(animalKey, food.rarity);
                      growTime = getProductGrowTime(animalKey, food.rarity);
                      console.log(`Full biopoints for ${animalKey}: ${biopoints}`);
                    } else {
                      console.log(`Insufficient seed types for ${animalKey}, missing:`, missingSeeds.map(s => `${s.name} ${s.rarity}`).join(', '));
                    }
                  }
                  const productMap = { birds: 'Eggs', cattle: 'Milk', fuzzies: 'Wool', piggies: 'Truffle' };
                  // Get animal common image
                  const animalImage = allAnimals[animalKey]?.variants?.[0]?.rarity?.[0]?.image;
                  let foodImage = null;
                  let foodRarity = null;
                  if (food && food.seeds.length > 0) {
                    foodRarity = food.seeds[0].rarity.toLowerCase();
                  } else if (missingSeeds.length > 0) {
                    foodRarity = missingSeeds[0].rarity.toLowerCase();
                  }
                  if (!food) {
                    // Find the lowest rarity food you can't make, and all missing seeds
                    const foodList = foodItems[animalKey]?.food || [];
                    for (let i = 0; i < foodList.length; i++) {
                      const candidate = foodList[i];
                      const missing = candidate.seeds && candidate.seeds.filter(req => {
                        const key = `${req.name.toLowerCase().replace(/ /g, '_')}-${req.rarity}`;
                        return (inventory.seeds[key] || 0) < 1;
                      });
                      if (missing && missing.length > 0) {
                        missingSeeds = missing;
                        break;
                      }
                    }
                  }
                  // For food images, use the getFoodImagePath function for consistency
                  if (food) {
                    if (animalKey === 'piggies') {
                      // For Piggies, use the main food image (truffle)
                      foodImage = getFoodImagePath(animalKey, food.rarity);
                    } else {
                      // For other animals, use the standard logic
                      const firstSeed = food.seeds?.[0];
                      if (firstSeed) {
                        const productSeedMap = { 
                          birds: 'egg', 
                          cattle: 'milk', 
                          fuzzies: 'fuzzies'
                        };
                        const seedId = productSeedMap[animalKey];
                        if (seedId) {
                          const rarity = foodRarity || firstSeed.rarity.toLowerCase();
                          foodImage = `/images/seeds/${seedId}/${rarity}/preview.png`;
                        }
                      }
                    }
                  }
                  // Get product image (use animal product and rarity)
                  let productImage = null;
                  if (food) {
                    const productIdMap = { 
                      birds: { type: 'vegetables', id: 'egg' }, 
                      cattle: { type: 'vegetables', id: 'milk' }, 
                      fuzzies: { type: 'vegetables', id: 'fuzzies' },
                      piggies: { type: 'vegetables', id: 'truffle', baseUrl: '/images' } // Path: /images/vegetables/truffle/common/preview.png
                    };
                    
                    // Debug: Log the expected image path
                    if (animalKey === 'piggies') {
                      console.log('Expected truffle image path:', `/images/vegetables/truffle/common/preview.png`);
                    }
                    const productInfo = productIdMap[animalKey];
                    // For Piggies, always use 'common' as the rarity for Truffle since it only has one variant
                    const rarity = animalKey === 'piggies' ? 'common' : food.rarity.toLowerCase();
                    const baseUrl = productInfo.baseUrl || '/images';
                    productImage = `${baseUrl}/${productInfo.type}/${productInfo.id}/${rarity}/preview.png`;
                    
                    // Debug log for Piggies
                    if (animalKey === 'piggies') {
                      console.log('Piggies product image path:', productImage);
                      console.log('Product info:', productInfo);
                      console.log('Rarity:', rarity);
                    }
                  }
                  // Initialize variables - use new standardized keys
                  const variants = allAnimals[animalKey]?.variants?.[0]?.rarity || [];
                  let totalFoodNeeded = 0;
                  let seedsNeeded = [];
                  let mergesNeeded = 0;
                  
                  // Check if we have all required seed types for the food
                  const hasAllRequiredSeeds = food && food.seeds.every(req => {
                    const seedNameLower = req.name.toLowerCase();
                    const rarityLower = req.rarity.toLowerCase();
                    
                    // Special handling for animal products (like Milk) vs regular seeds
                    if (seedNameLower === 'milk' || seedNameLower === 'eggs' || seedNameLower === 'wool') {
                      // This is an animal product, check if we can produce it
                      return canProduceAnimalProduct(seedNameLower, req.rarity, inventory.seeds, inventory.animals);
                    } else {
                      // This is a regular seed, check inventory
                      return Object.keys(inventory.seeds || {}).some(key => {
                        const [name, rarity] = key.split('-');
                        return name.toLowerCase() === seedNameLower && 
                               rarity.toLowerCase() === rarityLower &&
                               inventory.seeds[key] > 0;
                      });
                    }
                  });
                  
                  // Calculate food and merges if we have at least one of each required seed type
                  if (hasAllRequiredSeeds) {
                    if (animalKey === 'piggies') {
                      // Special handling for Piggies - use the dailyPerPiggy amount directly
                      const piggyCount = Object.entries(inventory.animals || {})
                        .filter(([key]) => key.startsWith('piggies-'))
                        .reduce((sum, [_, count]) => sum + (count || 0), 0);
                      
                      // For Piggies, we need 6 food items per piggy per day
                      totalFoodNeeded = piggyCount * 6; // 6 food items per piggy per day
                    } else {
                      // Standard calculation for other animals - use new standardized keys
                      variants.forEach(variant => {
                        const invKey = `${animalKey}-${variant.type}`;
                        const count = inventory.animals && inventory.animals[invKey] ? inventory.animals[invKey] : 0;
                        let growTime = allAnimals[animalKey]?.productionTime || 3600;
                        if (variant.growTime) growTime = variant.growTime;
                        const cycles = Math.floor(24 * 3600 / growTime);
                        totalFoodNeeded += count * cycles;
                      });
                    }
                    
                    // Calculate merges and seeds needed for 24h
                    if (food) {
                      if (animalKey === 'piggies') {
                        // For Piggies, calculate merges based on total food needed like other animals
                        mergesNeeded = Math.ceil(totalFoodNeeded / 3);
                        seedsNeeded = food.seeds.map(seed => ({
                          ...seed,
                          total: seed.amount * mergesNeeded  // Use mergesNeeded, not piggyCount
                        }));
                      } else if (food.seeds.length > 1) {  // Only show merges if more than one seed type is required
                        mergesNeeded = Math.ceil(totalFoodNeeded / 3);
                        seedsNeeded = food.seeds.map(seed => ({
                          ...seed,
                          total: seed.amount * mergesNeeded
                        }));
                      }
                    }
                  }
                  // Calculate products in 24h (0 if growTime is not set)
                  const baseProductsIn24h = growTime ? Math.floor((24 * 60 * 60) / growTime) : 0;
                  
                  // Calculate total products in 24h considering all animals and their rarities - use new standardized keys
                  let totalProductsIn24h = 0;
                  let animalCount = 0;
                  
                  // Process each animal in inventory
                  Object.entries(inventory.animals || {}).forEach(([key, count]) => {
                    const [animalType, rarity] = key.split('-');
                    if (animalType.toLowerCase() === animalKey.toLowerCase() && count > 0) {
                      const countNum = parseInt(count) || 0;
                      animalCount += countNum;
                      const multiplier = getRarityMultiplier(rarity);
                      totalProductsIn24h += baseProductsIn24h * countNum * multiplier;
                    }
                  });
                  
                  // If no animals found, use base calculation with animalCount (treated as Common)
                  if (totalProductsIn24h === 0 && animalCount > 0) {
                    totalProductsIn24h = baseProductsIn24h * animalCount;
                  }
                  // Calculate total biopoints based on total products and biopoints per product
                  const totalBiopoints = Math.floor(totalProductsIn24h * (biopoints || 0));
                  totalAnimalBiopoints += totalBiopoints;
                  
                  console.log(`Animal ${animalKey} biopoints:`, {
                    biopoints,
                    growTime,
                    totalProductsIn24h,
                    animalCount,
                    totalBiopoints
                  });

                  return (
                    <TableRow key={animalKey}>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          {animalImage && <RarityAvatar src={animalImage} alt={foodItems[animalKey].name} size={40} sx={{ verticalAlign: 'middle' }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          {foodImage && (food || missingSeeds.length > 0) ? (
                            <RarityAvatar src={foodImage} alt={food ? food.name : (missingSeeds.length > 0 ? missingSeeds[0].name : 'Missing')} size={40} sx={{ verticalAlign: 'middle' }} />
                          ) : (
                            missingSeeds.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                                {missingSeeds.map((seed, idx) => {
                                  // Handle special case for Milk which is a product, not a seed
                                  const seedName = seed.name === 'Milk' ? 'milk' : seed.name.toLowerCase().replace(/ /g, '_');
                                  return (
                                    <Tooltip key={idx} title={`${seed.name} (${seed.rarity})`} arrow>
                                      <RarityAvatar 
                                        src={`/images/vegetables/${seedName}/${seed.rarity.toLowerCase()}/preview.png`} 
                                        alt={seed.name} 
                                        size={40} 
                                        sx={{ 
                                          verticalAlign: 'middle', 
                                          filter: 'opacity(0.75) drop-shadow(0 0 0 red)' 
                                        }} 
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            ) : (
                              <span style={{ color: '#aaa' }}>None</span>
                            )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={typography.number}>{totalFoodNeeded > 0 ? `${totalFoodNeeded}` : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={typography.number}>{mergesNeeded > 0 ? `${mergesNeeded}` : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        {animalKey === 'piggies' ? (
                          // For Piggies, show both Legendary Milk and Legendary Mangold
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                            {seedsNeeded.map((seed, idx) => {
                              const seedName = seed.name.toLowerCase();
                              const seedKey = `${seedName.replace(/ /g, '_')}-${seed.rarity}`;
                              const isMilk = seedName.includes('milk');
                              
                              // Special handling for animal products (like Milk) vs regular seeds
                              let hasSeed = false;
                              if (isMilk) {
                                // This is an animal product, check if we can produce it
                                hasSeed = canProduceAnimalProduct(seedName, seed.rarity, inventory.seeds, inventory.animals);
                              } else {
                                // This is a regular seed, check inventory
                                hasSeed = (inventory.seeds[seedKey] || 0) > 0;
                              }
                              
                              return (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    color: hasSeed ? 'text.primary' : 'error.main'
                                  }}
                                >
                                  <RarityAvatar
                                    src={`/images/vegetables/${isMilk ? 'milk' : 'mangold'}/legendary/preview.png`}
                                    alt={`${seed.name} (${seed.rarity})`}
                                    size={30}
                                    sx={{ width: 30, height: 30 }}
                                  />
                                  <Typography component="div" variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    ×{seed.total}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : seedsNeeded.length > 0 ? (
                          // For other animals, show the required seeds
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                            {seedsNeeded.map((seed, idx) => {
                              const seedName = seed.name.replace(/\.$/, '');
                              const seedKey = `${seedName.toLowerCase().replace(/ /g, '_')}-${seed.rarity}`;
                              const hasSeed = (inventory.seeds[seedKey] || 0) > 0;
                              
                              return (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    color: hasSeed ? 'text.primary' : 'error.main'
                                  }}
                                >
                                  <RarityAvatar
                                    src={`/images/seeds/${seedName.toLowerCase()}/${seed.rarity.toLowerCase()}/preview.png`}
                                    alt={`${seedName} (${seed.rarity})`}
                                    size={30}
                                    sx={{ width: 30, height: 30 }}
                                  />
                                  <Typography component="div" variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    ×{seed.total}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography component="div" variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          {productImage && <RarityAvatar src={productImage} alt={productMap[animalKey]} size={40} sx={{ verticalAlign: 'middle' }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={typography.number}>{growTime !== null ? formatGrowTime(growTime) : <span style={{ color: '#aaa' }}>-</span>}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={typography.number}>{totalProductsIn24h > 0 ? `${totalProductsIn24h}` : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={typography.number}>{biopoints !== null ? biopoints : <span style={{ color: '#aaa' }}>-</span>}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={typography.number}>{totalBiopoints > 0 ? `${totalBiopoints}` : '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                });

                return (
                  <>
                    {rows}
                    {totalAnimalBiopoints > 0 && (
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell 
                          colSpan={10} 
                          align="center" 
                          sx={{ 
                            fontWeight: 'bold', 
                            p: 2,
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            '&.MuiTableCell-root': {
                              display: 'table-cell',
                              width: '100%',
                              maxWidth: '100%'
                            }
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            Total Biopoints from Animals: {totalAnimalBiopoints.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Food Seed Requirements Section */}
      <Box sx={{ width: 1200, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'primary.main' }}>
          Food Seed Direct Fusion Costs
        </Typography>
        <Typography component="div" variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          Direct Common seed costs for missing rarities after fusion
        </Typography>

        {(() => {
          const fusionCosts = calculateDirectFusionCosts(inventory);

          if (fusionCosts.length === 0) {
            return (
              <Box sx={{ textAlign: 'center', p: 3, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ color: 'success.main' }}>
                  ✅ Complete Collection!
                </Typography>
                <Typography component="div" variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  All food seed types already have complete collections.
                </Typography>
              </Box>
            );
          }

          return (
            <TableContainer sx={{ background: 'transparent', border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: 0, width: 1200, mx: 'auto', overflowX: 'hidden' }}>
              <Table size="small" sx={{ background: 'transparent', width: 1200, tableLayout: 'auto' }}>
                <TableHead sx={tableStyles(theme).tableHead}>
                  <TableRow>
                    <TableCell sx={{ ...tableStyles(theme).tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: 'rgba(25, 118, 210, 0.1)' }}>Seed Type</TableCell>
                    <TableCell sx={{ ...tableStyles(theme).tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: 'rgba(25, 118, 210, 0.1)' }}>Current Seeds</TableCell>
                    <TableCell sx={{ ...tableStyles(theme).tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: 'rgba(25, 118, 210, 0.1)' }}>Missing Rarities</TableCell>
                    <TableCell sx={{ ...tableStyles(theme).tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: 'rgba(25, 118, 210, 0.1)' }}>Common Seeds Needed</TableCell>
                    <TableCell sx={{ ...tableStyles(theme).tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: 'rgba(25, 118, 210, 0.1)' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fusionCosts.map((seedType, index) => {
                    const capitalizedType = seedType.type.charAt(0).toUpperCase() + seedType.type.slice(1);

                    // Get all missing rarities for complete collection
                    const missingRarities = [];
                    for (let i = 0; i < RARITIES.length; i++) {
                      const rarity = RARITIES[i];
                      const currentAfterFusion = seedType.afterFusion[rarity];
                      const target = 1;

                      if (currentAfterFusion < target) {
                        const needed = target - currentAfterFusion;
                        const commonCost = needed * RARITY_COSTS[i];
                        const rarityName = rarity.charAt(0).toUpperCase() + rarity.slice(1);

                        // Determine fusion path for this rarity
                        let fusionInfo = '';
                        let fusionDescription = '';
                        let isConsumedInFusion = false;

                        if (rarity === 'Legendary') {
                          // Check if fusion is possible with current inventory
                          const canFuseToEpic = seedType.current['Rare'] >= 2;
                          const canFuseToLegendary = seedType.current['Epic'] >= 2;

                          if (canFuseToEpic && canFuseToLegendary) {
                            fusionInfo = 'Created by: Rare → Epic → Legendary fusion chain';
                            fusionDescription = 'Created with Fusion';
                          } else if (canFuseToEpic) {
                            fusionInfo = 'Partial: Rare → Epic possible, need 2+ Epic for Legendary';
                            fusionDescription = 'Partially created with Fusion';
                          } else {
                            fusionInfo = 'Full chain: Common → Uncommon → Rare → Epic → Legendary';
                            fusionDescription = 'Can be created by full fusion chain';
                          }
                        } else if (rarity === 'Epic') {
                          fusionInfo = 'Used in: Epic → Legendary (then missing)';
                          fusionDescription = 'Needs to be refilled after fusion';
                          isConsumedInFusion = true;
                        } else if (rarity === 'Rare') {
                          fusionInfo = 'Used in: Rare → Epic (then missing)';
                          fusionDescription = 'Needs to be refilled after fusion';
                          isConsumedInFusion = true;
                        } else if (rarity === 'Uncommon') {
                          fusionInfo = 'Base rarity';
                          fusionDescription = 'Base rarity';
                        } else if (rarity === 'Common') {
                          fusionInfo = 'Base seed';
                          fusionDescription = 'Base rarity';
                        }

                        missingRarities.push({
                          rarity: rarityName,
                          needed,
                          commonCost,
                          fusionInfo,
                          fusionDescription,
                          isConsumedInFusion
                        });
                      }
                    }

                    return (
                      <TableRow key={index}>
                        <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontWeight: 'bold' }}>{capitalizedType}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {Object.entries(seedType.current).map(([rarity, count]) => {
                              if (count === 0) return null;
                              const rarityName = rarity.charAt(0).toUpperCase() + rarity.slice(1);
                              return (
                                <Box key={rarity} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <RarityAvatar
                                    src={`/images/seeds/${seedType.type}/${rarity.toLowerCase()}/preview.png`}
                                    alt={`${seedType.type} ${rarity}`}
                                    size={20}
                                    sx={{ width: 20, height: 20 }}
                                  />
                                  <Typography component="div" variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {rarityName}: {count}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {missingRarities.map((missing, idx) => (
                              <Box key={idx} sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  <RarityAvatar
                                    src={`/images/seeds/${seedType.type}/${missing.rarity.toLowerCase()}/preview.png`}
                                    alt={`${seedType.type} ${missing.rarity}`}
                                    size={20}
                                    sx={{ width: 20, height: 20 }}
                                  />
                                  <Typography component="div" variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    {missing.rarity}: Need {missing.needed}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                                  {missing.fusionDescription}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', display: 'block' }}>
                                  {missing.fusionInfo}
                                </Typography>
                                <Typography variant="caption" sx={{
                                  color: missing.isConsumedInFusion ? 'warning.main' : 'info.main',
                                  fontWeight: 'bold',
                                  display: 'block'
                                }}>
                                  {missing.isConsumedInFusion ? '(consumed in fusion)' : '(originally missing)'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block' }}>
                                  ({missing.commonCost} Common)
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <RarityAvatar
                              src={`/images/seeds/${seedType.type}/common/preview.png`}
                              alt={`${seedType.type} Common`}
                              size={24}
                              sx={{ width: 24, height: 24 }}
                            />
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mx: 0.5 }}>×</Typography>
                            <Typography variant="body1" sx={{
                              color: seedType.canComplete ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}>
                              {seedType.totalCommonNeeded}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Typography component="div" variant="body2" sx={{
                              color: seedType.canComplete ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}>
                              {seedType.canComplete ? '✅ Sufficient' : '❌ Insufficient'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </Box>
      {/* Container for both optimal upgrade path and best immediate upgrade */}
      {(bestImmediateUpgrade || trueOptimalUpgradePath) && (
        <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: '2px solid #1976d2' }}>
          {/* Optimal Upgrade Path for Maximum Passive Farming Output */}
          {trueOptimalUpgradePath && (
            <Box sx={{ mt: 3, width: 1200, mx: 'auto', background: 'rgba(255,255,255,0.03)', borderRadius: 2, p: 2, border: '2px solid #4caf50' }}>
          <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                True Optimal Upgrade Path for Passive Farming Output
          </Typography>
              {trueOptimalUpgradePath.steps.map((s, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                  <Tooltip title={`Upgrade ${s.count} ${s.from} beds`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {Array.from({ length: s.count }).map((_, i) => (
                    <RarityAvatar
                          key={i}
                          src={BED_DATA.find(b => b.rarity === s.from).image}
                          alt={s.from}
                          size={28}
                      sx={{ border: '1px solid #aaa' }}
                    />
                  ))}
                </Box>
              </Tooltip>
            <Typography component="div" variant="body2" sx={{ mx: 1 }}>
              ➔
            </Typography>
                  <Tooltip title={`Get ${s.newBeds} ${s.to} beds`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {Array.from({ length: s.newBeds }).map((_, i) => (
                    <RarityAvatar
                          key={i}
                          src={BED_DATA.find(b => b.rarity === s.to).image}
                          alt={s.to}
                          size={28}
                      sx={{ border: '1px solid #aaa' }}
                    />
                  ))}
                </Box>
              </Tooltip>
                  <Typography component="div" variant="body2" sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    (Cost:
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <RarityAvatar 
                        src="/images/fertilizers/basic/common/preview.png" 
                        alt="Fertilizer" 
                        size="md"
                        sx={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          minHeight: 40,
                          maxWidth: 40,
                          maxHeight: 40,
                          bgcolor: 'transparent',
                          '& img': {
                            width: '100% !important',
                            height: '100% !important',
                            minWidth: '100% !important',
                            minHeight: '100% !important',
                            maxWidth: '100% !important',
                            maxHeight: '100% !important',
                            objectFit: 'cover !important',
                            objectPosition: 'center',
                            p: 0,
                            margin: 0,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0
                          },
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                      {s.fert}
            </Box>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <RarityAvatar 
                        src="/images/icon_CFB.svg" 
                        alt="$CFB" 
                        size="md"
                        sx={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          minHeight: 40,
                          maxWidth: 40,
                          maxHeight: 40,
                          bgcolor: 'transparent',
                          '& img': {
                            width: '100% !important',
                            height: '100% !important',
                            objectFit: 'contain !important',
                            objectPosition: 'center',
                            p: 0,
                            margin: 'auto',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0
                          },
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                      {s.cfb}
          </Box>
                    )
                  </Typography>
                </Box>
              ))}
              <Typography component="div" variant="body2" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <b>Total Upgrade Cost:</b>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RarityAvatar 
                src="/images/fertilizers/basic/common/preview.png" 
                alt="Fertilizer" 
                size="md"
                sx={{
                  width: 40,
                  height: 40,
                  '& img': {
                    width: '100% !important',
                    height: '100% !important',
                    objectFit: 'contain !important',
                    objectPosition: 'center',
                    p: 0,
                    margin: 'auto',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 0.5
                }}
              />
              {trueOptimalUpgradePath.totalFert}
            </Box>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RarityAvatar 
                src="/images/icon_CFB.svg" 
                alt="$CFB" 
                size="md"
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  minHeight: 40,
                  maxWidth: 40,
                  maxHeight: 40,
                  bgcolor: 'transparent',
                  '& img': {
                    width: '100% !important',
                    height: '100% !important',
                    objectFit: 'contain !important',
                    objectPosition: 'center',
                    p: 0,
                    margin: 'auto',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 0.5
                }}
              />
              {trueOptimalUpgradePath.totalFri}
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Final Bed Configuration:</b> {Object.entries(trueOptimalUpgradePath.finalBeds).map(([rarity, count]) => count > 0 ? `${count} ${rarity}` : null).filter(Boolean).join(', ')}
          </Typography>
              <Typography component="div" variant="body2" sx={{ mt: 1 }}>
                <b>New Total Biopoints:</b> {trueOptimalUpgradePath.newTotal} (Gain: +{trueOptimalUpgradePath.gain})
              </Typography>
              {/* Show seed assignments for the true optimal configuration */}
              {trueOptimalUpgradePath.assignments && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                    What to plant after all upgrades:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {trueOptimalUpgradePath.assignments.map((a, idx) => (
                  <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120, p: 1, border: '1px solid #eee', borderRadius: 2 }}>
                    <Tooltip title={`${a.plot.name} (${a.plot.rarity})`} arrow>
                      <RarityAvatar src={a.plot.image} alt={a.plot.name} size={40} sx={{ mb: 0.5 }} />
                    </Tooltip>
                    <Tooltip title={`${a.seed.name} (${a.seed.rarity})`} arrow>
                      <RarityAvatar src={a.seed.image} alt={a.seed.name} size={40} />
                    </Tooltip>
                    <Typography component="div" variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>{a.seed.name} <b>{a.seed.rarity}</b></Typography>
                    <Typography component="div" variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>
                      Total: <b>{typeof a.total !== 'undefined' ? a.total : (a.plot.productAmount * a.seed.biopoints)}</b> BP
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
          {/* Best Immediate Upgrade Step (below the optimal path) */}
          {bestImmediateUpgrade && (
            <>
              <Box sx={{ my: 2, borderBottom: '1px solid #ccc' }} />
              <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 1 }}>
                Which upgrade brings the most benefit?
          </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title={`Upgrade 2 ${bestImmediateUpgrade.from} beds`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {Array.from({ length: 2 }).map((_, i) => (
                    <RarityAvatar
                      key={i}
                        src={BED_DATA.find(b => b.rarity === bestImmediateUpgrade.from).image}
                        alt={bestImmediateUpgrade.from}
                      size={28}
                      sx={{ border: '1px solid #aaa' }}
                    />
                  ))}
                </Box>
              </Tooltip>
              <Typography variant="body2" sx={{ mx: 1 }}>
                ➔
              </Typography>
                <Tooltip title={`Get 1 ${bestImmediateUpgrade.to} bed`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RarityAvatar
                      src={BED_DATA.find(b => b.rarity === bestImmediateUpgrade.to).image}
                      alt={bestImmediateUpgrade.to}
                      size={28}
                      sx={{ border: '1px solid #aaa' }}
                    />
                </Box>
              </Tooltip>
              <Typography variant="body2" sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                (Cost:
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RarityAvatar 
                    src="/images/fertilizers/basic/common/preview.png" 
                    alt="Fertilizer" 
                    size={40}
                    sx={{ 
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      minHeight: 40,
                      '& img': {
                        width: '100% !important',
                        height: '100% !important',
                        maxWidth: '100% !important',
                        maxHeight: '100% !important',
                        objectFit: 'contain !important',
                        objectPosition: 'center',
                        display: 'block !important',
                        p: 0,
                        margin: 'auto',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                      },
                      position: 'relative',
                      overflow: 'hidden',
                      mr: 0.5
                    }}
                  />
                    {bestImmediateUpgrade.fert}
                </Box>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RarityAvatar 
                    src="/images/icon_CFB.svg" 
                    alt="$CFB" 
                    size="md"
                    sx={{ 
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      minHeight: 40,
                      maxWidth: 40,
                      maxHeight: 40,
                      bgcolor: 'transparent',
                      '& img': {
                        width: '100% !important',
                        height: '100% !important',
                        maxWidth: '100% !important',
                        maxHeight: '100% !important',
                        objectFit: 'contain !important',
                        objectPosition: 'center',
                        display: 'block !important',
                        p: 0,
                        margin: 'auto',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                      },
                      position: 'relative',
                      overflow: 'hidden',
                      mr: 0.5
                    }}
                  />
                    {bestImmediateUpgrade.cfb}
                </Box>
                )
              </Typography>
                <Typography component="div" variant="body2" sx={{ ml: 2, color: 'success.main' }}>
                  <b>Biopoints Gain: +{bestImmediateUpgrade.gain}</b>
          </Typography>
                <Typography component="div" variant="body2" sx={{ ml: 2, color: 'primary.main' }}>
                  <b>Total Biopoints after upgrade: {bestImmediateUpgrade.totalBiopoints}</b>
          </Typography>
              </Box>
              {/* Show what to plant after this upgrade */}
              {bestImmediateUpgrade.assignments && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                    What to plant after this upgrade:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {bestImmediateUpgrade.assignments.map((a, idx) => (
                  <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120, p: 1, border: '1px solid #eee', borderRadius: 2 }}>
                    <Tooltip title={`${a.plot.name} (${a.plot.rarity})`} arrow>
                      <RarityAvatar src={a.plot.image} alt={a.plot.name} size={40} sx={{ mb: 0.5 }} />
                    </Tooltip>
                    <Tooltip title={`${a.seed.name} (${a.seed.rarity})`} arrow>
                      <RarityAvatar src={a.seed.image} alt={a.seed.name} size={40} />
                    </Tooltip>
                    <Typography component="div" variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>{a.seed.name} <b>{a.seed.rarity}</b></Typography>
                    <Typography component="div" variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>
                      Total: <b>{typeof a.total !== 'undefined' ? a.total : (a.plot.productAmount * a.seed.biopoints)}</b> BP
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Recommendations; 